import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Loader, Text } from 'src/@vymo/ui/atoms';
import { locale } from 'src/i18n';
import keys from 'src/i18n/keys';
import { HOME_DASHBOARD } from 'src/modules/constants';
import { useAppDispatch } from 'src/store/hooks';
import { applyPersistedSessionToRedux } from 'src/workspace/rehydrateWebSession';
import { renderModule } from 'src/workspace/slice';
import { resetHomeDashboard } from '../slice';
import {
  checkAuthRedirectionValidity,
  getAuthRedirectionToken,
  initAuthRedirectionSession,
  logoutAuthRedirection,
} from '../thunk';
import {
  getAuthRedirectErrorMessage,
  getAuthRedirectPayload,
  getDecodedAccessToken,
  getStoredAuthToken,
} from './queries';
import styles from '../index.module.scss';

function AuthRedirection(props) {
  const { rid, sid } = props;
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleAuthRedirect = useCallback(async () => {
    let authToken = getStoredAuthToken();
    const { redirectErrorMessage, clientId, accessToken, oneTapLogin } =
      getAuthRedirectPayload(searchParams);

    try {
      if (redirectErrorMessage) {
        await logoutAuthRedirection();
        return;
      }

      if (oneTapLogin) {
        const response = await checkAuthRedirectionValidity(
          clientId,
          getDecodedAccessToken(accessToken),
        );
        authToken = response.authToken || '';
      } else if (rid && !authToken) {
        authToken = await getAuthRedirectionToken(rid, sid);
      }

      if (authToken) {
        await initAuthRedirectionSession(authToken);
      }

      await applyPersistedSessionToRedux();
      dispatch(resetHomeDashboard());
      dispatch(renderModule({ id: HOME_DASHBOARD, props: {} }));
      setLoading(false);
    } catch (err: any) {
      setError(getAuthRedirectErrorMessage(err));
      setLoading(false);
    }
  }, [dispatch, rid, searchParams, sid]);

  useEffect(() => {
    handleAuthRedirect();
  }, [dispatch, handleAuthRedirect, rid, searchParams, sid]);

  return (
    <div className={styles.authRedirect}>
      <div className={styles.authRedirect__card}>
        {loading && (
          <>
            <Loader />
            <Text type="h6">{locale(keys.SIGNIN_INTO_VYMO)}</Text>
          </>
        )}
        {Boolean(error && !loading) && (
          <>
            <Text type="h6" variant="error">
              {error}
            </Text>
            <Button
              type="outlined"
              variant="subtle"
              onClick={logoutAuthRedirection}
            >
              {locale(keys.BACK_TO_LOGIN)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthRedirection;
